import { Criticidad, type CriticidadValor } from '../../domain/value-objects/Criticidad';
import { registrarError, traducirErrorSupabase } from '../../domain/errores';
import { aBooleano, aNumero, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';
import { filtroDemo } from './FiltroDemo';
import { leerNombres } from './consultas';

export type TipoUmbral = 'analogico' | 'digital';

/** Nombres de los días, indexados como los guarda la base: 0 = domingo. */
export const DIAS_SEMANA = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
] as const;

/**
 * Ventana horaria en la que el umbral vigila.
 * Sin ninguna ventana, el umbral vigila todo el tiempo.
 */
export interface VentanaHoraria {
  /** El identificador de la franja sí es numérico. */
  id?: number;
  /** `HH:mm` */
  horaInicio: string;
  /** `HH:mm` */
  horaFin: string;
  /** Días en los que aplica; 0 = domingo. */
  dias: number[];
}

/** Orden automática a un actuador cuando el umbral se dispara. */
export interface ReglaActuador {
  id?: string;
  /** Los actuadores sí se identifican con un número. */
  actuadorId: number;
  actuadorNombre: string;
  /** `set` o `pulse`, igual que en el centro de control. */
  tipoOrden: 'set' | 'pulse';
  /** Contenido de la orden: `{v:'ON'}` o `{pulse_ms:500}`. */
  carga: Record<string, unknown>;
  /** Deshace la orden sola cuando la condición vuelve a normal. */
  restablecerAutomatico: boolean;
  activo: boolean;
}

/**
 * Umbral tal como se muestra en la pantalla de configuración: con los nombres
 * de sensor, nodo, área y cliente ya resueltos.
 */
export interface UmbralConfigurado {
  /** Los umbrales se identifican con un UUID, no con un número. */
  id: string;
  sensorId: number | null;
  sensorNombre: string;
  criticidad: Criticidad;
  activo: boolean;
  retencion: boolean;
  notificar: boolean;
  mensaje: string | null;
  retardoSegundos: number | null;
  /** Sólo en analógicos. */
  valorMinimo: number | null;
  valorMaximo: number | null;
  histeresis: number | null;
  inhibicionArranqueSegundos: number | null;
  /** Sólo en digitales. */
  estadoEsperado: boolean | null;
  nodoNombre: string | null;
  areaNombre: string | null;
  clienteNombre: string | null;
  /** Ventanas horarias en las que vigila; vacío = siempre. */
  horarios: VentanaHoraria[];
  /** Órdenes automáticas a actuadores al dispararse. */
  reglas: ReglaActuador[];
}

/** Campos que la pantalla puede modificar. */
export interface DatosUmbralAnalogico {
  sensor_id: number;
  valor_min: number | null;
  valor_max: number | null;
  deadband: number | null;
  debounce_segundos: number;
  inhibicion_arranque_segundos: number;
  criticidad: CriticidadValor;
  mensaje: string | null;
  activo: boolean;
  latched: boolean;
  notificar: boolean;
}

export interface DatosUmbralDigital {
  sensor_id: number;
  estado_esperado: boolean;
  debounce_segundos: number;
  criticidad: CriticidadValor;
  mensaje: string | null;
  activo: boolean;
  latched: boolean;
  notificar: boolean;
}

const VISTAS: Readonly<Record<TipoUmbral, string>> = {
  analogico: 'vw_umbrales_analogicos_completos',
  digital: 'vw_umbrales_digitales_completos',
};

const TABLAS: Readonly<Record<TipoUmbral, string>> = {
  analogico: 'umbrales_analogicos',
  digital: 'umbrales_digitales',
};

function aUmbral(fila: Record<string, unknown>): UmbralConfigurado {
  return {
    id: aTexto(fila.id) ?? '',
    sensorId: aNumero(fila.sensor_id),
    sensorNombre: aTexto(fila.sensor_nombre) ?? 'Sensor desconocido',
    criticidad: Criticidad.from(aTexto(fila.criticidad)),
    activo: aBooleano(fila.activo) ?? true,
    retencion: aBooleano(fila.latched) ?? false,
    notificar: aBooleano(fila.notificar) ?? false,
    mensaje: aTexto(fila.mensaje),
    retardoSegundos: aNumero(fila.debounce_segundos),
    valorMinimo: aNumero(fila.valor_min),
    valorMaximo: aNumero(fila.valor_max),
    histeresis: aNumero(fila.deadband),
    inhibicionArranqueSegundos: aNumero(fila.inhibicion_arranque_segundos),
    estadoEsperado: aBooleano(fila.estado_esperado),
    nodoNombre: aTexto(fila.nodo_nombre),
    areaNombre: aTexto(fila.area_nombre),
    clienteNombre: aTexto(fila.cliente_nombre),
    horarios: [],
    reglas: [],
  };
}

/** Las horas llegan como `HH:mm:ss`; en pantalla sólo interesan horas y minutos. */
function aHoraCorta(valor: unknown): string {
  return (aTexto(valor) ?? '').slice(0, 5);
}

function aDias(valor: unknown): number[] {
  if (!Array.isArray(valor)) return [0, 1, 2, 3, 4, 5, 6];

  const dias = valor
    .map((d) => aNumero(d))
    .filter((d): d is number => d !== null && d >= 0 && d <= 6);
  return dias.length > 0 ? dias : [0, 1, 2, 3, 4, 5, 6];
}

/**
 * Configuración de los umbrales de sensores.
 *
 * La lectura va contra vistas que ya traen resueltos los nombres, pero que
 * —a diferencia de otras— no exponen los identificadores de cliente, área ni
 * nodo. Por eso el filtrado por ubicación se hace comparando nombres sobre lo
 * ya cargado, y no se puede delegar al servidor.
 */
export class RepositorioUmbrales {
  async listar(tipo: TipoUmbral): Promise<UmbralConfigurado[]> {
    const { data, error } = await filtroDemo
      .aplicar(VISTAS[tipo], supabase.from(VISTAS[tipo]).select('*'))
      .order('sensor_nombre', { ascending: true });

    if (error) throw traducirErrorSupabase(error);

    const umbrales = ((data ?? []) as Record<string, unknown>[]).map(aUmbral);
    if (umbrales.length === 0) return umbrales;

    const ids = umbrales.map((u) => u.id).filter((id) => id !== '');

    // Horarios y reglas son información complementaria: si fallan, los
    // umbrales se muestran igual en vez de dejar la pantalla vacía.
    const [horarios, reglas] = await Promise.all([
      tipo === 'digital'
        ? this.leerHorarios(ids)
        : Promise.resolve(new Map<string, VentanaHoraria[]>()),
      this.leerReglas(tipo, ids),
    ]);

    for (const umbral of umbrales) {
      umbral.horarios = horarios.get(umbral.id) ?? [];
      umbral.reglas = reglas.get(umbral.id) ?? [];
    }

    return umbrales;
  }

  private async leerHorarios(ids: readonly string[]): Promise<Map<string, VentanaHoraria[]>> {
    const mapa = new Map<string, VentanaHoraria[]>();
    if (ids.length === 0) return mapa;

    const { data, error } = await supabase
      .from('umbrales_digitales_horarios')
      .select('id, umbral_digital_id, hora_inicio, hora_fin, dias')
      .in('umbral_digital_id', [...ids])
      .order('hora_inicio', { ascending: true });

    if (error) {
      registrarError('umbrales.horarios', error);
      return mapa;
    }

    for (const fila of (data ?? []) as Record<string, unknown>[]) {
      const umbralId = aTexto(fila.umbral_digital_id);
      if (umbralId === null) continue;

      const lista = mapa.get(umbralId) ?? [];
      lista.push({
        id: aNumero(fila.id) ?? undefined,
        horaInicio: aHoraCorta(fila.hora_inicio),
        horaFin: aHoraCorta(fila.hora_fin),
        dias: aDias(fila.dias),
      });
      mapa.set(umbralId, lista);
    }

    return mapa;
  }

  private async leerReglas(
    tipo: TipoUmbral,
    ids: readonly string[],
  ): Promise<Map<string, ReglaActuador[]>> {
    const mapa = new Map<string, ReglaActuador[]>();
    if (ids.length === 0) return mapa;

    const { data, error } = await supabase
      .from('umbrales_actuadores_auto')
      .select('id, umbral_id, actuador_id, cmd_kind, cmd_payload, restablecer_automatico, activo')
      .eq('tipo_umbral', tipo)
      .in('umbral_id', [...ids]);

    if (error) {
      registrarError('umbrales.reglas', error);
      return mapa;
    }

    const filas = (data ?? []) as Record<string, unknown>[];
    const nombres = await leerNombres(
      'v3_vw_actuadores_control_ui',
      filas.map((f) => aNumero(f.actuador_id)).filter((id): id is number => id !== null),
    );

    for (const fila of filas) {
      const umbralId = aTexto(fila.umbral_id);
      const actuadorId = aNumero(fila.actuador_id);
      if (umbralId === null || actuadorId === null) continue;

      const lista = mapa.get(umbralId) ?? [];
      lista.push({
        id: aTexto(fila.id) ?? undefined,
        actuadorId,
        actuadorNombre: nombres.get(actuadorId) ?? `Actuador ${actuadorId}`,
        tipoOrden: aTexto(fila.cmd_kind) === 'pulse' ? 'pulse' : 'set',
        carga: (fila.cmd_payload as Record<string, unknown>) ?? {},
        restablecerAutomatico: aBooleano(fila.restablecer_automatico) ?? false,
        activo: aBooleano(fila.activo) ?? true,
      });
      mapa.set(umbralId, lista);
    }

    return mapa;
  }

  /**
   * Reemplaza las ventanas horarias de un umbral digital.
   *
   * Se borran las anteriores y se insertan las nuevas: son pocas y no tienen
   * identidad propia para el usuario, así que comparar una por una sólo
   * añadiría complejidad sin beneficio.
   */
  async guardarHorarios(umbralId: string, horarios: readonly VentanaHoraria[]): Promise<void> {
    const { error: errorBorrado } = await supabase
      .from('umbrales_digitales_horarios')
      .delete()
      .eq('umbral_digital_id', umbralId);

    if (errorBorrado) throw traducirErrorSupabase(errorBorrado);
    if (horarios.length === 0) return;

    const { error } = await supabase.from('umbrales_digitales_horarios').insert(
      horarios.map((h) => ({
        umbral_digital_id: umbralId,
        hora_inicio: h.horaInicio,
        hora_fin: h.horaFin,
        dias: h.dias,
      })),
    );

    if (error) throw traducirErrorSupabase(error);
  }

  /** Reemplaza las órdenes automáticas asociadas a un umbral. */
  async guardarReglas(
    tipo: TipoUmbral,
    umbralId: string,
    reglas: readonly ReglaActuador[],
  ): Promise<void> {
    const { error: errorBorrado } = await supabase
      .from('umbrales_actuadores_auto')
      .delete()
      .eq('tipo_umbral', tipo)
      .eq('umbral_id', umbralId);

    if (errorBorrado) throw traducirErrorSupabase(errorBorrado);
    if (reglas.length === 0) return;

    const { error } = await supabase.from('umbrales_actuadores_auto').insert(
      reglas.map((r) => ({
        umbral_id: umbralId,
        tipo_umbral: tipo,
        actuador_id: r.actuadorId,
        cmd_kind: r.tipoOrden,
        cmd_payload: r.carga,
        restablecer_automatico: r.restablecerAutomatico,
        activo: r.activo,
      })),
    );

    if (error) throw traducirErrorSupabase(error);
  }

  /** Crea el umbral y devuelve su identificador, necesario para sus anexos. */
  async crear(tipo: TipoUmbral, datos: DatosUmbralAnalogico | DatosUmbralDigital): Promise<string> {
    // La tabla de destino se decide en tiempo de ejecución, así que el tipado
    // por tabla del SDK no puede aplicarse aquí; la forma correcta de los
    // datos la garantizan `DatosUmbralAnalogico` y `DatosUmbralDigital`.
    const { data, error } = await supabase
      .from(TABLAS[tipo])
      .insert(datos as unknown as Record<string, unknown>)
      .select('id')
      .single();

    if (error) throw traducirErrorSupabase(error);
    return aTexto((data as Record<string, unknown>).id) ?? '';
  }

  async actualizar(
    tipo: TipoUmbral,
    id: string,
    datos: Partial<DatosUmbralAnalogico & DatosUmbralDigital>,
  ): Promise<void> {
    const { error } = await supabase
      .from(TABLAS[tipo])
      .update(datos as unknown as Record<string, unknown>)
      .eq('id', id);

    if (error) throw traducirErrorSupabase(error);
  }

  async eliminar(tipo: TipoUmbral, id: string): Promise<void> {
    const { error } = await supabase.from(TABLAS[tipo]).delete().eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  /** Activa o desactiva sin abrir el formulario completo. */
  async cambiarActivo(tipo: TipoUmbral, id: string, activo: boolean): Promise<void> {
    return this.actualizar(tipo, id, { activo });
  }
}

export const repositorioUmbrales = new RepositorioUmbrales();
